import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ChakraProvider, Spinner, Center, extendTheme } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Base from './Base';
import baseTheme from './theme';
import { organizationSchema, websiteSchema } from './utils/seo';
import { AuthProvider } from './context/AuthContext';

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '284733452563-8vdkf1gss72e09lfvh9hmvbv3o30dlss.apps.googleusercontent.com';

// Extend theme with Modal configuration to prevent overlay issues
const theme = extendTheme({
  ...baseTheme,
  components: {
    ...baseTheme.components,
    Modal: {
      baseStyle: {
        overlay: {
          bg: 'blackAlpha.600',
        },
        dialog: {
          bg: 'white',
        },
      },
    },
  },
});

// Lazy load all page components
const Home = lazy(() => import('./Pages/Home'));
const Category = lazy(() => import('./Pages/Category'));
const Signup = lazy(() => import('./Pages/singup'));
const Product = lazy(() => import('./Pages/product'));
const ProductAll = lazy(() => import('./Pages/productall'));
const PriceCalculator = lazy(() => import('./Pages/PriceCalculator'));
const PaymentSuccess = lazy(() => import('./Pages/PaymentSuccess'));
const Cart = lazy(() => import('./Pages/cart'));
const CartNew = lazy(() => import('./Pages/cartnew'));
const Profile = lazy(() => import('./Pages/profile'));
const ChangePassword = lazy(() => import('./Pages/changepassword'));
const Address = lazy(() => import('./Pages/address'));
const Notification = lazy(() => import('./Pages/notification'));
const Orders = lazy(() => import('./Pages/orders'));
const TrackOrder = lazy(() => import('./Pages/trackorder'));
const OrderDetails = lazy(() => import('./Pages/orderDetails'));
const CancelRequest = lazy(() => import('./Pages/cancelrequest'));
const Contact = lazy(() => import('./Pages/contact'));
const Faq = lazy(() => import('./Pages/faq'));
const Career = lazy(() => import('./Pages/career'));
const Information = lazy(() => import('./Pages/information'));
const Offers = lazy(() => import('./Pages/offers'));
const OfferProfile = lazy(() => import('./Pages/offerprofile'));
const PrintCashHistory = lazy(() => import('./Pages/printcashhistory'));
const OrderChat = lazy(() => import('./Pages/orderchat'));
const SearchProduct = lazy(() => import('./Pages/serachproduct'));


// Loading component
const LoadingSpinner = () => (
  <Center h="100vh">
    <Spinner
      thickness="4px"
      speed="0.65s"
      emptyColor="gray.200"
      color="primary.500"
      size="xl"
    />
  </Center>
);

const App = () => {
  console.log('App component rendering...');
  console.log('About to render BrowserRouter with Base wrapper');

  // Clean up stale modal overlays on mobile
  useEffect(() => {
    let cleanupTimeout = null;
    let wasModalActive = document.body.classList.contains('chakra-modal-active');

    const cleanupOverlays = () => {
      // Only clean up if no modal is active
      const isModalActive = document.body.classList.contains('chakra-modal-active');
      if (!isModalActive) {
        // Find all portal roots (where Chakra renders modals)
        const portals = document.querySelectorAll('[data-chakra-portal]');

        portals.forEach(portal => {
          // Only remove empty portals
          const hasActiveContent = portal.querySelector('.chakra-modal__content');
          if (!hasActiveContent) {
            portal.remove();
          }
        });
      }
    };

    // Watch for modal state changes
    const observer = new MutationObserver(() => {
      const isModalActive = document.body.classList.contains('chakra-modal-active');

      // Only trigger cleanup when modal transitions from active to inactive
      if (wasModalActive && !isModalActive) {
        // Clear any pending cleanup
        if (cleanupTimeout) clearTimeout(cleanupTimeout);
        // Schedule cleanup after modal close animation
        cleanupTimeout = setTimeout(cleanupOverlays, 350);
      }

      wasModalActive = isModalActive;
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ChakraProvider
        theme={theme}
        resetCSS
        portalZIndex={1400}
        toastOptions={{
          portalZIndex: 1500
        }}
      >
        {/* Global SEO schemas */}
        <Helmet>
          {/* Organization Schema */}
          <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
          </script>
          {/* Website Schema with Search Action */}
          <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
          </script>
        </Helmet>

        <BrowserRouter basename={process.env.PUBLIC_URL || ''}>
          <AuthProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <Base>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/cart/:category/:product" element={<Cart />} />
                <Route path="/print-cash-history" element={<PrintCashHistory />} />
                <Route path="/offers-profile" element={<OfferProfile />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/order-chat/:id/:no" element={<OrderChat />} />
                <Route path="/information/:seo" element={<Information />} />
                <Route path="/search/:seo" element={<SearchProduct />} />
                <Route path="/faq/:id" element={<Faq />} />
                <Route path="/career" element={<Career />} />
                <Route path="/cartnew" element={<CartNew />} />
                <Route path="/register" element={<Signup />} />
                <Route path="/login" element={<Signup />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/address" element={<Address />} />
                <Route path="/return-request/:id" element={<CancelRequest />} />
                <Route path="/notification" element={<Notification />} />
                <Route path="/track-order" element={<TrackOrder />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders-details/:id" element={<OrderDetails />} />
                <Route path="/success/:id" element={<PaymentSuccess />} />
                <Route path="/register/:code" element={<Signup />} />
                <Route path="/category/:cid" element={<Category />} />
                <Route path="/product/:cid/:pid" element={<Product />} />
                <Route path="/product-all" element={<ProductAll />} />
                <Route path="/pricecalculator" element={<PriceCalculator />} />
                <Route path="/pricecalculator/:pid" element={<PriceCalculator />} />

              </Routes>
            </Base>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ChakraProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
